import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useAssistant } from '@/shell/AppShell'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { RadarSweep } from '@/components/forge/RadarSweep'
import { Eyebrow, Num } from '@/components/forge/primitives'
import { anomalyPlaceName, hasRcaContent, rcaDescription, toRcaSections } from '@/features/anomalies/api'
import { formatInteger } from '@/lib/format'
import { ON_TINT_CLASS } from '@/lib/tone'
import { cn } from '@/lib/cn'

/**
 * Analysis · RCA — the analysis, read.
 *
 * ── This was the AI Synopsis tab, and the tab is gone ────────────────────
 * The screen used to open on a tab called "AI Synopsis" holding the report, with
 * an "Analysis" tab beside it holding a hardcoded version of the same argument.
 * Two tabs for one subject, one of them invented. The report moved here, where
 * the rest of the analysis already lived, and the synopsis tab went with the
 * demo it was duplicating.
 *
 * ── What came across, and the one thing that did not ─────────────────────
 * The report, the "not investigated" prompt with its Investigate button, and the
 * verification disclaimer are all here. The rail beside them — the Window and
 * Provenance cards — was dropped, by decision: `window_start`, `window_end`,
 * `last_detected_at` and `updated_at` are on the header and in the timeline, and
 * a sticky column restating them cost the argument a third of its width.
 */
export function RcaView({ anomaly }) {
  const { t } = useTranslation(['app', 'common'])
  /* The dock, read here rather than threaded down from the page: this is the one
     control in the module that talks to it, and a prop passed through three
     components to reach a single button is indirection for its own sake. */
  const { investigate, analysing, busy } = useAssistant()

  const analysed = hasRcaContent(anomaly)

  if (analysed) {
    return (
      <div className="flex flex-col gap-4">
        <RcaReport content={anomaly.content} />
        <p className="m-0 text-body-sm text-ink-mute italic">
          {t('anomalies.detail.rcaDisclaimer')}
        </p>
      </div>
    )
  }

  /* An analysis is being written for THIS record, right now. Checked against the
     id rather than as a boolean: the dock follows the reader around, and a radar
     on the anomaly you navigated to would be reporting the previous one's work
     as this one's. */
  if (analysing === anomaly.id) return <Analysing />

  /* ── The empty state, and what it can now offer ────────────────────────
     It offered nothing for a while, and correctly: the old Investigate posted to
     `POST /anomalies/{id}/rca/request`, a route that no longer exists, and a
     button that looks live and does nothing is worse than no button. There is
     still no request endpoint and there never will be — an analysis is produced
     by the engine off a COPILOT TURN.

     The client can send that turn. `investigate` grounds the dock on this
     anomaly and asks it, with `request_rca` in the turn's own metadata, which is
     the whole of the trigger. One path: whatever this button does, the reader
     could have typed, and the answer arrives in the same place either way. */
  return (
    <RcaPrompt
      title={t('anomalies.detail.notInvestigated')}
      body={t('anomalies.detail.notInvestigatedBody')}
    >
      <ForgeButton
        variant="ai"
        /* The assistant takes one question at a time and there is no queue. */
        disabled={busy}
        onClick={() =>
          investigate({
            id: anomaly.id,
            serialNumber: anomaly.serialNumber,
            assetName: anomalyPlaceName(anomaly.hierarchy),
          })
        }
      >
        <Icon name="ai" size="sm" />
        {t('anomalies.detail.investigate')}
      </ForgeButton>
    </RcaPrompt>
  )
}

/**
 * The radar, and what it is doing.
 *
 * ── It was retired, and the reason it came back ───────────────────────────
 * It belonged to one state — requested, running, nothing back yet — and that state
 * stopped being observable when the RCA row and its `queued` status were deleted.
 * It is observable again, but from the other end: an analysis is a copilot TURN
 * now, and a turn is something this client is holding open. So the radar tracks the
 * turn rather than the record. It is up while the answer is being written to this
 * screen and comes down when the turn ends, however it ends.
 *
 * What that costs, stated rather than hidden: navigate away and the stream is
 * aborted, so the radar goes and the analysis carries on being written without it.
 * The server offers nothing to poll — `has_analysis` is a boolean over whether
 * `content` is empty — so "an analysis is running somewhere" is not a thing this
 * client can know. Fixing that needs a progress signal the API does not have; it is
 * in BACKEND-GAPS.md.
 *
 * ── One phase, not the prototype's four ───────────────────────────────────
 * The prototype cycles "Investigating anomaly" → "Analysing root cause" →
 * "Building recommendations" → "Preparing work order" on a timer. That is a
 * fabricated pipeline: the engine reports no stages, so announcing that it has
 * moved on to drafting a corrective plan would be this screen inventing progress it
 * cannot see. PRODUCT.md's fourth principle is that provenance is always on screen;
 * a fake stage is the exact opposite.
 *
 * `role="status"` rather than a bare `aria-live`, so the heading and the caption
 * are announced as one update instead of two.
 */
function Analysing() {
  const { t } = useTranslation(['app', 'common'])

  return (
    <div role="status" className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <RadarSweep className="mb-4" />
      <h2 className="m-0 text-title">{t('anomalies.detail.analysing')}</h2>
      <p className="m-0 max-w-[52ch] text-body-sm text-ink-dim">
        {t('anomalies.detail.analysingBody')}
      </p>
    </div>
  )
}

/**
 * The centred prompt the design puts under the header card.
 *
 * ── Flat violet, not the prototype's gradient ─────────────────────────────
 * The prototype draws this mark as a `linear-gradient(135deg,#BF5AF2,#7B3FE4)` disc
 * inside a radial halo. DESIGN.md forbids both and names violet-on-an-AI-affordance
 * as the clearest generated-software tell there is. The radar earns its single-hue
 * alpha falloff because a sweep IS one; a static disc earns nothing, so this is the
 * violet tint with the node glyph — the convention every other AI surface uses.
 */
function RcaPrompt({ title, body, children }) {
  return (
    <div className="flex flex-col items-center gap-4 px-6 py-14 text-center">
      <span
        aria-hidden="true"
        className="flex size-14 items-center justify-center rounded-full tint-violet"
      >
        <Icon name="ai" size="lg" className="text-violet" />
      </span>

      <div className="flex flex-col items-center gap-2">
        <h2 className="m-0 text-title">{title}</h2>
        <p className="m-0 max-w-[52ch] text-body-sm text-ink-dim">{body}</p>
      </div>

      {children ? <div className="flex flex-col items-center gap-2.5">{children}</div> : null}
    </div>
  )
}

/**
 * The analysis: a report, not a form full of paragraphs.
 *
 * ── Rebuilt against the shape the engine actually writes ─────────────────
 * This rendered `what_happened`, `fault_domain` and `ranked_causes[]`, each cause
 * with a likelihood, supporting and against evidence and a verification step. The
 * engine stopped producing that shape: `rca_engine/content.py` now emits one prose
 * string and a set of flat `{text, description}` lists, and a test pins the set.
 *
 * So the screen went on rendering keys the data no longer had — every card empty,
 * "Not stated" under each heading, and nothing broken enough to notice.
 *
 * ── Uniform sections, because the data is uniform ────────────────────────
 * Every list is the same `{text, description}` pair, so there is one row component
 * rather than a bespoke card per key. That is a real loss of texture — `CauseRow`
 * gave a cause a severity-ranked leading edge from its likelihood, which made the
 * ranking visible from the side of the page — and it is not recoverable: likelihood
 * is not a field any more, it is a sentence folded into the description. Inventing a
 * rank from the list order would be this screen asserting a confidence the engine
 * did not state.
 *
 * What survives is the order, which is editorial and is in `RCA_SECTIONS`: symptoms,
 * then the causes, then the gaps, then what was excluded. `recommended_actions` is
 * not here — it is on the Action tab, with the rest of what to do.
 *
 * ── Empty sections are dropped, not drawn ────────────────────────────────
 * `toRcaSections` filters them. An analysis that ruled nothing out has nothing to
 * say under "Ruled out", and a column of empty headings reads as a failed render
 * rather than as a complete report.
 */
function RcaReport({ content }) {
  const { t } = useTranslation(['app', 'common'])

  const description = rcaDescription(content)
  const sections = toRcaSections(content)

  return (
    <>
      {description ? (
        <RcaCard eyebrow={t('anomalies.detail.description')} tone="violet">
          <Narrative text={description} />
        </RcaCard>
      ) : null}

      {sections.map((section) => (
        <RcaCard
          key={section.key}
          eyebrow={t(`anomalies.detail.section.${section.key}`)}
          tone={SECTION_TONE[section.key] ?? null}
          count={section.items.length}
        >
          <FindingList sectionKey={section.key} items={section.items} />
        </RcaCard>
      ))}
    </>
  )
}

/**
 * One card of the report.
 *
 * The eyebrow is the card's whole chrome — no title bar, no icon, no border under
 * the heading. DESIGN.md opens every card with a 10px/700 uppercase label in Ink
 * Muted and this is that, unchanged, which is why three of these stacked read as one
 * document rather than as three widgets.
 *
 * `count` is offered because these sections are lists, and a reader deciding whether
 * to expand their attention wants the length before the content. It is a plain
 * figure rather than a badge: a count is not a state.
 *
 * `tone` colours the card's leading edge and its eyebrow together, so a section's
 * hue is stated twice — on the 3px edge that makes the column scannable from the
 * side, and on the heading that names what the hue means. One prop rather than two,
 * because a card whose stripe and heading disagreed would be a bug nobody could see.
 */
export function RcaCard({ eyebrow, tone = null, count, children, className }) {
  return (
    <Panel stripe={tone} className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <Eyebrow as="h3" className={cn('m-0', tone && ON_TINT_CLASS[tone])}>
          {eyebrow}
        </Eyebrow>
        {count != null ? (
          <Num className="text-micro font-normal text-ink-mute">{formatInteger(count)}</Num>
        ) : null}
      </div>
      {children}
    </Panel>
  )
}

/**
 * A list of `{text, description}` findings.
 *
 * Shared with the Action tab's Recommended actions card, which draws the same
 * shape out of the same `content` object — one component, so a finding looks the
 * same wherever the engine's rows are shown.
 */
export function FindingList({ sectionKey, items }) {
  return (
    <ul className="m-0 flex list-none flex-col gap-3 p-0">
      {items.map((item, index) => (
        <li key={`${sectionKey}-${index}`} className="flex flex-col gap-1">
          <span className="text-body font-semibold text-ink">{item.text}</span>
          {/* Optional by construction — `data_gaps` sets it to `""` on every row —
              so an absent description is not drawn as a missing one. */}
          {item.description ? (
            <span className="text-body-sm text-ink-dim">{item.description}</span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/**
 * The description, with each line's lead-in emboldened.
 *
 * The engine writes this as one newline-separated block of "Label — sentence"
 * lines: Window, Order (cascades only), one line per sensor, then a blank line
 * and the fault-domain sentence. `whitespace-pre-line` keeps the breaks and the
 * blank line. Flat, they read as an undifferentiated wall and the reader has to
 * find the em dash on every line to work out what each one is about.
 *
 * So the lead-in is bolded — by SHAPE, not by a list of known labels. The set is
 * open: the generator is still being built and a hardcoded list would silently stop
 * bolding the day it adds a seventh line. The rule is "short run of text before an
 * em dash, at the start of a line", which is the actual convention the payload
 * follows.
 *
 * The guard is the length cap. Without it, a prose sentence that happens to contain
 * an em dash would get its first clause bolded, which is worse than not bolding at
 * all — so anything past a plausible label length is left alone. The text is
 * rendered as text either way; nothing here interprets it as markup.
 */
function Narrative({ text }) {
  return (
    <p className="m-0 text-body whitespace-pre-line">
      {String(text)
        .split('\n')
        .map((line, index, all) => {
          const split = line.indexOf(' — ')
          /* 48, not 28: one line per SENSOR is now a label too, and sensor
             names run long — "Strainer Differential Pressure" is 30. */
          const isLabel = split > 0 && split <= 48
          return (
            <Fragment key={index}>
              {isLabel ? (
                <>
                  <strong className="font-semibold text-ink">{line.slice(0, split)}</strong>
                  {line.slice(split)}
                </>
              ) : (
                line
              )}
              {index < all.length - 1 ? '\n' : null}
            </Fragment>
          )
        })}
    </p>
  )
}

/**
 * The hue each section carries, where it carries one.
 *
 * Three of the five, deliberately: the causes are the conclusion, the gaps are
 * what the conclusion does not cover, and what was ruled out is dismissed. The
 * other two take no stripe, because a column where every card is coloured is a
 * column where no colour means anything.
 */
const SECTION_TONE = {
  root_causes: 'violet',
  ruled_out: 'dismissed',
  data_gaps: 'amber',
}
